import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Download, Insights } from '@mui/icons-material'
import { Box, Button, IconButton, Link, Stack, TableContainer, Tooltip, Typography } from '@mui/material'

import { GRTable, GRTableColumn, SortOrder } from '../../../../../components/GRTable/GRTable'
import { GameIcon } from '../../../../../features/game'
import appStoreService from '../../../../../services/AppStoreService'
import { AdDataRow, getAppStoreGenreName, getSubgenreMatch, toPercent } from '../helpers/adDataHelpers'

type CustomProps = {
  downloadingAppId?: number
  onDownloadJson: (row: AdDataRow) => void
  onShowAnalyses: (row: AdDataRow) => void
}

type AdDataResultsTableProps = CustomProps & { rows: AdDataRow[]; isLoading?: boolean }

const missing = '–'
const unranked = Number.MAX_SAFE_INTEGER

const RankCell: FC<{ rank?: number; genreRanks: { [id: string]: number } }> = ({ rank, genreRanks }) => (
  <Box>
    <Typography variant="body2" fontWeight="bold">
      {rank ?? missing}
    </Typography>
    {Object.entries(genreRanks).map(([id, genreRank]) => (
      <Typography key={id} variant="caption" display="block" color="text.secondary">
        {getAppStoreGenreName(appStoreService.appStoreGenreMap, id)}: {genreRank}
      </Typography>
    ))}
  </Box>
)

const percentText = (value?: number) => {
  const percent = toPercent(value)
  return percent === undefined ? missing : `${percent}%`
}

const columnMinWidths = { game: 150, appId: 120, subgenre: 140, artStyle: 90, artGenre: 110, percent: 100, rank: 88, analyses: 72 }
// GRTable clips columns wider than its container, so the table is kept at least this wide and scrolls instead
const tableMinWidth =
  columnMinWidths.game +
  columnMinWidths.appId +
  columnMinWidths.subgenre +
  columnMinWidths.artStyle +
  columnMinWidths.artGenre +
  2 * columnMinWidths.percent +
  2 * columnMinWidths.rank +
  columnMinWidths.analyses

const leftAligned = (minWidth: number) => ({
  cellProps: { align: 'left' as const },
  headerCellProps: { align: 'left' as const, sx: { minWidth } },
})

export const AdDataResultsTable: FC<AdDataResultsTableProps> = ({ rows, isLoading, downloadingAppId, onDownloadJson, onShowAnalyses }) => {
  const { t } = useTranslation()

  const initialColumns: GRTableColumn<AdDataRow, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-service-management:ad_data_game'),
        accessor: ({ row }) => (
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ maxWidth: 280 }}>
            <GameIcon src={row.icon ?? ''} gameName={row.name} size="small" />
            <Box minWidth={0}>
              <Typography variant="body2" fontWeight="bold">
                {row.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {row.artist || missing}
              </Typography>
            </Box>
          </Stack>
        ),
        sortable: true,
        sortAccessor: ({ row }) => row.name.toLowerCase(),
        ...leftAligned(columnMinWidths.game),
      },
      {
        labelAccessor: t('internal-service-management:ad_data_app_id'),
        accessor: ({ row, customTableProps }) => (
          <Box>
            <Link href={`https://apps.apple.com/us/app/id${row.appId}`} target="_blank" rel="noopener noreferrer" fontWeight="bold" variant="body2">
              {row.appId}
            </Link>
            <Button
              size="small"
              startIcon={<Download />}
              onClick={() => customTableProps?.onDownloadJson(row)}
              disabled={customTableProps?.downloadingAppId === row.appId}
              aria-label={t('internal-service-management:ad_data_download_json_for', { name: row.name })}
              sx={{ display: 'flex', mt: 0.5, px: 0.5, minWidth: 0, whiteSpace: 'nowrap', fontSize: 'caption.fontSize' }}
            >
              {t('internal-service-management:ad_data_download_json')}
            </Button>
          </Box>
        ),
        sortable: true,
        sortAccessor: ({ row }) => row.appId,
        ...leftAligned(columnMinWidths.appId),
      },
      {
        labelAccessor: t('internal-service-management:ad_data_subgenre_column'),
        accessor: ({ row }) => {
          const match = getSubgenreMatch(row)
          const color = match === 'match' ? 'success.main' : match === 'mismatch' ? 'error.main' : undefined
          return (
            <Box data-subgenre-match={match ?? 'unknown'}>
              <Typography variant="body2" fontWeight="bold" color={color}>
                {row.modelSubgenre ?? missing}
              </Typography>
              <Typography variant="caption" color={color ?? 'text.secondary'}>
                {row.conventionalSubgenre ?? missing}
              </Typography>
            </Box>
          )
        },
        sortable: true,
        sortAccessor: ({ row }) => (row.modelSubgenre ?? '').toLowerCase(),
        ...leftAligned(columnMinWidths.subgenre),
      },
      {
        labelAccessor: t('internal-service-management:ad_data_art_style'),
        accessor: ({ row }) => row.artStyle ?? missing,
        sortable: true,
        sortAccessor: ({ row }) => row.artStyle ?? '',
        ...leftAligned(columnMinWidths.artStyle),
      },
      {
        labelAccessor: t('internal-service-management:ad_data_art_genre'),
        accessor: ({ row }) => row.artGenre ?? missing,
        sortable: true,
        sortAccessor: ({ row }) => row.artGenre ?? '',
        ...leftAligned(columnMinWidths.artGenre),
      },
      {
        labelAccessor: t('internal-service-management:ad_data_saturation'),
        accessor: ({ row }) => percentText(row.saturation),
        sortable: true,
        sortAccessor: ({ row }) => row.saturation ?? -1,
        headerCellProps: { sx: { minWidth: columnMinWidths.percent } },
      },
      {
        labelAccessor: t('internal-service-management:ad_data_lightness'),
        accessor: ({ row }) => percentText(row.lightness),
        sortable: true,
        sortAccessor: ({ row }) => row.lightness ?? -1,
        headerCellProps: { sx: { minWidth: columnMinWidths.percent } },
      },
      {
        labelAccessor: t('internal-service-management:ad_data_sgr'),
        accessor: ({ row }) => <RankCell rank={row.srank} genreRanks={row.appGenreSranks} />,
        sortable: true,
        sortOrder: SortOrder.ASC,
        defaultSortOrder: SortOrder.ASC,
        sortAccessor: ({ row }) => row.srank ?? unranked,
        ...leftAligned(columnMinWidths.rank),
      },
      {
        labelAccessor: t('internal-service-management:ad_data_sdr'),
        accessor: ({ row }) => <RankCell rank={row.sdrank} genreRanks={row.appGenreSdranks} />,
        sortable: true,
        defaultSortOrder: SortOrder.ASC,
        sortAccessor: ({ row }) => row.sdrank ?? unranked,
        ...leftAligned(columnMinWidths.rank),
      },
      {
        labelAccessor: t('internal-service-management:ad_data_analyses'),
        accessor: ({ row, customTableProps }) => (
          <Tooltip title={t('internal-service-management:ad_data_show_analyses')}>
            <IconButton
              color="primary"
              onClick={() => customTableProps?.onShowAnalyses(row)}
              aria-label={t('internal-service-management:ad_data_show_analyses_for', { name: row.name })}
            >
              <Insights />
            </IconButton>
          </Tooltip>
        ),
        headerCellProps: { sx: { minWidth: columnMinWidths.analyses } },
      },
    ],
    [t]
  )

  const [columns, setColumns] = useState(initialColumns)
  useEffect(() => setColumns(initialColumns), [initialColumns])

  return (
    <TableContainer>
      <Box minWidth={tableMinWidth}>
        <GRTable
          rows={rows}
          columns={columns}
          onColumnsUpdated={setColumns}
          rowIdKey={(row) => String(row.appId)}
          customProps={{ downloadingAppId, onDownloadJson, onShowAnalyses }}
          isLoading={isLoading}
          noRowsLabel={t('internal-service-management:ad_data_no_rows')}
          hoverable
          striped
        />
      </Box>
    </TableContainer>
  )
}
